import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useAuth } from '../store';
import { isUltraAdmin } from '../utils';
import { getAllEventIds, loadEvent } from '../services/firestore';
import type { Event } from '../types';

/**
 * User Events Page (Ultra Admin Only)
 * Access any event for any user and act as admin
 */
export function UserEventsPage() {
  const navigate = useNavigate();
  const { currentUser, isAuthenticated } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Check ultra-admin access
  useEffect(() => {
    if (!isAuthenticated || !currentUser || !isUltraAdmin(currentUser)) {
      navigate('/');
      return;
    }
  }, [isAuthenticated, currentUser, navigate]);

  // Load all events
  useEffect(() => {
    const loadAllEvents = async () => {
      try {
        const eventIds = await getAllEventIds();
        const loadedEvents: Event[] = [];
        
        for (const eventId of eventIds) {
          const event = await loadEvent(eventId);
          if (event) {
            loadedEvents.push(event);
          }
        }
        
        // Sort by creation date (newest first)
        loadedEvents.sort((a, b) => b.createdAt - a.createdAt);
        setEvents(loadedEvents);
        setFilteredEvents(loadedEvents);
      } catch (err) {
        console.error('Failed to load events:', err);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser && isUltraAdmin(currentUser)) {
      loadAllEvents();
    }
  }, [currentUser]);

  // Filter events based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredEvents(events);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = events.filter(event => 
      event.name.toLowerCase().includes(query) ||
      event.id.toLowerCase().includes(query) ||
      event.adminUsername?.toLowerCase().includes(query)
    );
    setFilteredEvents(filtered);
  }, [searchQuery, events]);

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-secondary-text">Loading events...</p>
        </div>
      </div>
    );
  }

  if (!currentUser || !isUltraAdmin(currentUser)) {
    return null;
  }

  const getStatusBadge = (event: Event) => {
    if (event.finished) {
      return <Badge variant="default" size="sm">Finished</Badge>;
    }
    if (event.status === 'live') {
      return <Badge variant="error" size="sm">Live</Badge>;
    }
    return <Badge variant="info" size="sm">Upcoming</Badge>;
  };

  return (
    <div className="min-h-screen bg-primary-bg pb-20">
      {/* Header */}
      <div className="bg-secondary-bg border-b border-border p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-primary-text">User Events</h1>
            <p className="text-sm text-secondary-text">Ultra Admin: Access any event</p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/account')}
          >
            Back
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Search */}
        <Card padding="md">
          <Input
            label="Search Events"
            placeholder="Search by event name, ID, or creator..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Card>

        {/* Stats */}
        <Card padding="md">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-sm text-secondary-text mb-1">Total Events</div>
              <div className="text-2xl font-bold text-primary-text">{events.length}</div>
            </div>
            <div>
              <div className="text-sm text-secondary-text mb-1">Filtered</div>
              <div className="text-2xl font-bold text-primary-text">{filteredEvents.length}</div>
            </div>
            <div>
              <div className="text-sm text-secondary-text mb-1">Published</div>
              <div className="text-2xl font-bold text-primary-text">
                {events.filter(e => e.published).length}
              </div>
            </div>
          </div>
        </Card>

        {/* Events List */}
        {filteredEvents.length === 0 ? (
          <Card padding="lg">
            <div className="text-center py-8">
              <p className="text-secondary-text">No events found</p>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredEvents.map((event) => (
              <Card key={event.id} padding="md">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-primary-text">{event.name}</h3>
                    <div className="flex gap-2 items-center mt-2">
                      {getStatusBadge(event)}
                      {!(event.published ?? true) && (
                        <Badge variant="warning" size="sm">Draft</Badge>
                      )}
                      {event.isPublic && (
                        <Badge variant="success" size="sm">Public</Badge>
                      )}
                    </div>
                    <p className="text-sm text-secondary-text mt-2">
                      Created by: {event.adminUsername || 'Unknown'} • {new Date(event.createdAt).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-muted-text mt-1">
                      ID: {event.id}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-tertiary-bg rounded-lg">
                  <div>
                    <div className="text-xs text-secondary-text mb-1">Participants</div>
                    <div className="font-semibold text-primary-text">
                      {event.participants.length}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-secondary-text mb-1">Fights</div>
                    <div className="font-semibold text-primary-text">
                      {event.fights.length}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-secondary-text mb-1">Total Bets</div>
                    <div className="font-semibold text-primary-text">
                      {event.participants.reduce((sum, p) => sum + p.bets.length, 0)}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    fullWidth
                    onClick={() => navigate(`/admin/${event.id}`)}
                  >
                    Enter as Admin
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    fullWidth
                    onClick={() => navigate(`/event/${event.id}`)}
                  >
                    View Dashboard
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

